<template>
  <section
    :class="[$style.banner, { [$style.bannerCompact]: isCompact }]"
    :aria-label="t('aria')"
  >
    <header :class="$style.head" v-if="!isCompact">
      <h2 :class="$style.title">{{ stage.name }}</h2>
      <pickem-countdown v-if="timerTo" :to="timerTo" :class="$style.timerChip">
        <ui-icon name="time" :class="$style.timerIcon" />
      </pickem-countdown>
      <span :class="$style.lockChip">
        <ui-icon name="lock-circled" :class="$style.lockChipIcon" />
        {{ t('lockedChip') }}
      </span>
    </header>

    <div :class="$style.body">
      <div
        :class="[
          $style.description,
          { [$style.descriptionSingle]: isSingleCondition }
        ]"
      >
        <span :class="$style.lockIcon">
          <ui-icon name="lock-circled" :class="$style.lockIconSvg" />
        </span>
        <div :class="$style.descriptionText">
          <h3 :class="$style.headline">
            {{ t('headline', { name: stage.name }) }}
          </h3>
          <p :class="$style.subhead">
            {{ t('preface') }}
            <span v-if="!isSingleCondition" :class="$style.combineMode">
              {{ combineModeLabel }}
            </span>
            <template v-if="!isSingleCondition">
              {{
                actualUnlock.combine_mode === 'AND'
                  ? t('postfaceAnd')
                  : t('postfaceOr')
              }}
            </template>
            <template v-else>
              {{ t('postfaceSingle') }}
            </template>
          </p>
        </div>
      </div>

      <ul :class="[$style.list, $style[`list-${columnsModifier}`]]">
        <li
          v-for="(condition, index) in conditions"
          :key="condition.id"
          :class="$style.cell"
        >
          <unlock-condition-item
            :condition="condition"
            :index="index + 1"
            :stage="stage"
            :unlock="actualUnlock"
            @open="onOpenAction"
          />
        </li>
      </ul>
    </div>
  </section>
</template>

<i18n lang="yaml">
ru:
  aria: Условия разблокировки этапа
  lockedChip: Заблокировано
  headline: '{name} заблокирован'
  preface: 'Для доступа выполни '
  postfaceSingle: 'условие:'
  postfaceAnd: ' условия:'
  postfaceOr: ' условий:'
  combine:
    AND: все
    OR: одно из
</i18n>

<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import PickemCountdown from '../../common/Countdown.vue'
import UnlockConditionItem from './UnlockConditionItem.vue'
import { useUnlockStore } from '../../../stores/unlock'
import { usePickemAnalytics } from '../../../composables/use-pickem-analytics'
import { useTournamentStore } from '../../../stores/tournament'
import type { Stage, StageUnlock, UnlockCondition } from '../../../types'

defineOptions({ name: 'PickemUnlockBanner' })

const props = defineProps<{
  stage: Stage
  // unlock приходит из полного Stage, но для рефреша используем стор —
  // компонент сам берёт актуальный snapshot и подписывается на focus.
  unlock: StageUnlock
  // isCompact - true, если отображается ВНУТРИ этапа, когда нужно показать preview
  isCompact?: boolean
}>()

const { t } = useI18n()
const { trackUnlockClick } = usePickemAnalytics()
const tournamentStore = useTournamentStore()

const unlockStore = useUnlockStore()

// При монтировании — кладём snapshot из полного Stage в стор и подписываемся
// на focus/visibilitychange. При смене stage.id — отписываемся и
// переподписываемся.
let unsubscribe: (() => void) | null = null

onMounted(() => {
  unlockStore.setLocal(props.stage.id, props.unlock)
  unsubscribe = unlockStore.subscribeToFocus(props.stage.id)
})

onBeforeUnmount(() => {
  unsubscribe?.()
  unsubscribe = null
})

// Актуальный unlock — либо рефрешенный из стора, либо изначальный.
const actualUnlock = computed<StageUnlock>(
  () => unlockStore.get(props.stage.id) ?? props.unlock
)

const conditions = computed<UnlockCondition[]>(
  () => actualUnlock.value.conditions
)

const isSingleCondition = computed(() => conditions.value.length === 1)

const combineModeLabel = computed(() =>
  actualUnlock.value.combine_mode === 'AND' ? t('combine.AND') : t('combine.OR')
)

// Сетка карточек условий: 1 → full-width card, 2 → row, 3+ → auto-fit.
const columnsModifier = computed(() => {
  const n = conditions.value.length
  if (n === 1) return 'one'
  if (n === 2) return 'two'
  return 'many'
})

// Таймер — это lock_at. У заблокированного этапа значение времени до
// разблокировки в shape напрямую не приходит, но `lock_at` обозначает дедлайн
// окна (когда окно «откроется-закроется»). На макете — зелёная плашка с
// «5ч 13м». Если stage.lock_at = null — не показываем.
const timerTo = computed(() => props.stage.lock_at ?? null)

// Когда юзер кликает по action_url — открываем во вкладке и сразу же
// помечаем себе, что надо проверить статус по возвращении: единичный
// принудительный refresh после короткой задержки. focus-listener тоже
// сработает, но эта явная инициация снижает зависимость от того,
// в какой момент браузер нам пришлёт событие.
const onOpenAction = (condition: UnlockCondition, conditionIndex: number) => {
  if (!condition.action_url || condition.user_satisfied) return

  if (!tournamentStore.tournament) return

  trackUnlockClick({
    stage: props.stage,
    unlock: actualUnlock.value,
    condition,
    conditionIndex,
    tournament: tournamentStore.tournament
  })

  setTimeout(() => {
    unlockStore.refresh(props.stage.id).catch(() => undefined)
  }, 1500)
}
</script>

<style lang="scss" module>
.banner {
  display: flex;
  flex-direction: column;
  gap: 2.4em;
  padding: 2.4em;
  background-color: color-get(--root-rgb-bg-tone-dark);
  border-radius: 0 0 1.2em 1.2em;

  @include down(sm) {
    background: rgba(var(--root-rgb-bg-special-1), 1);
  }
}

// Если отображается внутри этапа с превью
.bannerCompact {
  padding: 1.6em;

  background-color: color-get(--root-rgb-bg-dark);
  border-radius: 1.2em;

  .body {
    flex-direction: row;
    gap: 2em;

    @include down(md) {
      flex-direction: column;
    }
  }

  .description {
    flex-direction: row;
    align-items: flex-start;

    max-width: 23em;
    text-align: left;

    @include down(md) {
      flex-direction: column;
      align-items: center;
      text-align: center;
      max-width: 100%;
    }
  }

  .descriptionSingle {
    min-width: 32em;
    max-width: none;
  }

  .lockIcon {
    width: 3.2em;
    height: 3.2em;

    @include down(md) {
      width: 6em;
      height: 6em;
    }
  }

  .lockIconSvg {
    width: 2.2em;
    height: 2.2em;

    @include down(md) {
      width: 3em;
      height: 4em;
    }
  }

  .headline {
    @include typo-get(headline-m);
  }

  .list-one,
  .list-two {
    max-width: 100%;
  }
}

.head {
  display: flex;
  align-items: center;
  gap: 1.2em;
  flex-wrap: wrap;
}

.title {
  margin: 0;
  @include typo-get(headline-m);
  color: color-get(--root-rgb-text-1);
}

.timerChip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.4em 0.6em;
  border-radius: 0.4em;
  background-color: color-get(--root-rgb-accent-2);
  color: color-get(--root-rgb-text-inverse);
  @include typo-get(subhead-s);
  white-space: nowrap;
}

.timerIcon {
  width: 1em;
  flex-shrink: 0;
}

.lockChip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.3em 0.8em 0.3em 0.8em;
  border: 1px solid color-get(--root-rgb-accent-4);
  border-radius: 0.4em;
  background-color: transparent;
  color: color-get(--root-rgb-accent-4);
  @include typo-get(body-s);
  white-space: nowrap;
}

.lockChipIcon {
  width: 1.2em;
  flex-shrink: 0;
}

.body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3.2em;
  width: 100%;
}

.description {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8em;
  text-align: center;
}

.lockIcon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 8em;
  height: 8em;
  border-radius: 50%;
  background-color: color-get(--root-rgb-bg-card);
  color: color-get(--root-rgb-accent-4);
}

.lockIconSvg {
  width: 4em;
  height: 4em;
}

.descriptionText {
  display: flex;
  flex-direction: column;
  gap: 0.8em;
}

.headline {
  margin: 0;
  @include typo-get(headline-l);
  color: color-get(--root-rgb-text-1);

  @include down(md) {
    @include typo-get(headline-m);
  }
}

.subhead {
  margin: 0;
  @include typo-get(body-m);
  color: color-get(--root-rgb-text-2);
}

.combineMode {
  text-decoration: underline;
  text-underline-offset: 0.15em;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.cell {
  display: flex;
  min-width: 0;
}

.list-one,
.list-two {
  display: flex;
  align-items: stretch;
  width: 100%;
  max-width: 61.4em;
  margin-inline: auto;
}

.list-one {
  gap: 0;
}

.list-one > .cell {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.list-two {
  flex-wrap: wrap;
  gap: 1.2em;
}

.list-two > .cell {
  flex: 1 1 0;
  min-width: 0;

  @include down(sm) {
    flex: 1 1 100%;
  }
}

.list-many {
  display: grid;
  align-items: stretch;
  gap: 1.2em;
  grid-template-columns: repeat(auto-fit, minmax(20em, 1fr));
}
</style>
